import { CircularProgress } from '@material-ui/core'
import PropTypes from 'prop-types'
import React, { useMemo, useRef } from 'react'
import { useGetInitialLinkedData, useGetLevelEntityGraphByGraphType } from '../../../../api/entityGraphs'
import { useAppContext } from '../../../../redux/slices/appContext'
import { getGraphTypeDefaultConfig, getGraphTypePlugin } from '../GraphTypes'
import { EntityGraphProvider } from './EntityGraphProvider'
import { buildInitialLayout } from './utils'

export const Wrapper = ({
  children, levelTypeId, levelId: _levelId, graphType
}) => {
  const { clientId } = useAppContext()
  const levelId = useMemo(() => {
    if (_levelId) return _levelId
    if (levelTypeId === 201) {
      return clientId
    }
  }, [_levelId, levelTypeId, clientId])

  const { data, isLoading, isFetched } = useGetLevelEntityGraphByGraphType({
    levelTypeId,
    levelId,
    graphType,
    includeLinkedEntityData: true
  }, {
    enabled: !!levelId
  })

  const gt = data?.graphType ?? graphType
  const plugin = useMemo(() => getGraphTypePlugin(gt), [gt])

  const graphConfig = useMemo(() => {
    if (data?.entityGraphTypeDetails) {
      return {
        ...(data?.entityGraphTypeDetails?.config ?? {}),
        ...(data?.config ?? {})
      }
    }
    return {
      ...(getGraphTypeDefaultConfig(gt) ?? {})
    }
  }, [data?.config, data?.entityGraphTypeDetails, gt])

  const { data: initialLinkedEntityData, isLoading: isLoadingInitialLinkedEntityData } = useGetInitialLinkedData({
    config: graphConfig,
    level: { levelId, levelTypeId },
    plugin,
    queryOptions: {
      enabled: !!plugin && !data?.nodes?.length && !!levelId && isFetched
    }
  })

  const hasBuiltInitialLayout = useRef(false)
  const initialLayoutCache = useRef(null)

  const providerData = useMemo(() => {
    if ((!data && !plugin) || (!!plugin && isLoadingInitialLinkedEntityData)) return null

    let finalData = data?.data
    if (plugin) {
      const noNodes = !(finalData?.nodes?.length > 0)
      if (noNodes && !!plugin?.createInitial) {
        if (!hasBuiltInitialLayout.current) {
          const laidOut = buildInitialLayout(plugin, graphConfig, { levelId, levelTypeId })

          if (initialLinkedEntityData?.slotDataArray?.length > 0) {
            const slotNodes = laidOut.nodes.filter(n => n.data?.isSlot)

            slotNodes.forEach((node, index) => {
              const slotData = initialLinkedEntityData.slotDataArray[index]
              if (slotData) {
                node.data = {
                  label: slotData?.label,
                  description: slotData?.description,
                  ...node.data,
                  entityProps: slotData
                }
              }
            })
          }

          initialLayoutCache.current = laidOut
          hasBuiltInitialLayout.current = true
        }
        finalData = {
          ...(finalData ?? {}),
          nodes: initialLayoutCache.current.nodes,
          edges: initialLayoutCache.current.edges || []
        }
      }
    }

    return {
      data: finalData,
      config: graphConfig,
      graphType: data?.graphType ?? graphType,
      entityGraphId: data?.entityGraphId ?? null,
      linkedEntityData: {
        ...(data?.linkedEntityData ?? {}),
        ...(initialLinkedEntityData?.linkedEntityData ?? {})
      },
      levelTypeId,
      levelId
    }
  }, [data, plugin, isLoadingInitialLinkedEntityData, graphConfig, graphType, initialLinkedEntityData, levelTypeId, levelId])

  if (isLoading || (plugin && isLoadingInitialLinkedEntityData)) {
    return (
      <div className='flex items-center justify-center w-full h-full'>
        <CircularProgress />
      </div>
    )
  }
  if (!providerData) {
    return null
  }

  return (
    <EntityGraphProvider {...providerData}>
      <div className='w-full relative'>
        {children}
      </div>
    </EntityGraphProvider>
  )
}

Wrapper.propTypes = {
  children: PropTypes.node.isRequired,
  levelTypeId: PropTypes.number.isRequired,
  levelId: PropTypes.number,
  graphType: PropTypes.string.isRequired
}
